/**
 * Application-specific wrapper for useAuth hook
 *
 * Adds one behaviour on top of the library hook: sign-out revokes the Cognito
 * refresh token through our own API *before* handing off to the hosted-UI
 * logout redirect. The library's `signoutRedirect` clears local state and then
 * navigates to Cognito's `/logout`, and that navigation can be rejected with a
 * 403 when the hosted-UI cookie jar is oversized — leaving a live refresh token
 * behind. Revoking first closes that gap without changing sign-out semantics:
 * the hosted-UI redirect still runs, so the SSO session is still ended.
 *
 * See `@/platform/auth/revokeSession` and
 * `docs/tech/ADRs/ADR-0003-cognito-silent-signin.md`.
 *
 * Usage:
 * ```typescript
 * import { useAuth } from '@/hooks/useAppAuth'
 *
 * function MyComponent() {
 *   const { isAuthenticated, signinRedirect, signoutRedirect } = useAuth()
 *   // Revocation + Cognito logout are both handled by signoutRedirect
 * }
 * ```
 */

import { useCallback } from 'react'
import { useAuth as useLibraryAuth } from '@revalize/auth/frontend'
import { revokeSession } from '@/platform/auth/revokeSession'

/**
 * Wrapper hook that provides the library's useAuth with revoking sign-out
 *
 * No configuration needed - AuthProvider handles Cognito config via context
 *
 * @returns Auth state and methods with automatic Cognito logout support
 */
export function useAuth(): ReturnType<typeof useLibraryAuth> {
  const auth = useLibraryAuth()

  const signoutRedirect = useCallback(
    async (args?: unknown): Promise<void> => {
      // Revoke before the redirect: once the browser leaves the app we lose the
      // opportunity, and the redirect itself is not guaranteed to arrive.
      await revokeSession(auth.oidcProfile?.refresh_token)

      return auth.signoutRedirect(args)
    },
    [auth],
  )

  return { ...auth, signoutRedirect }
}
